import { useMemo, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize
} from "shared/components";
import { IconButtonVariant } from "shared/components/IconButton";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import Drawer from "react-modern-drawer";
import { PropertyMultiSelectDropdown } from "views/rent-reporting/components/FilterDrawer/components/PropertyMultiSelectDropdown";
import { MultiSelectDropdown } from "views/rent-reporting/components/FilterDrawer/components/MultiSelectDropdown";
import { DateRange } from "views/rent-reporting/components/FilterDrawer/components/DateRange";
import { getOperatingStatesAndProvinces } from "api/landlord/landlord";
import type { IdentityTheftFilters } from "../types";
import { INITIAL_FILTERS } from "../utils";

type Props = {
  open: boolean;
  onClose: () => void;
  filters: IdentityTheftFilters;
  onApply: (filters: IdentityTheftFilters) => void;
};

export const IdentityTheftFilterDrawer = ({
  open,
  onClose,
  filters,
  onApply
}: Props) => {
  const [draft, setDraft] = useState<IdentityTheftFilters>(filters);

  useEffect(() => {
    if (open) setDraft({ ...filters });
  }, [open, filters]);

  const { data: operatingStates } = useQuery({
    queryKey: ["OPERATING_STATES_AND_PROVINCES"],
    queryFn: getOperatingStatesAndProvinces
  });
  const uniqueStates = useMemo(
    () => operatingStates?.operating_states_and_provinces || [],
    [operatingStates]
  );

  const handleClearAll = () => setDraft({ ...INITIAL_FILTERS });

  const handleApply = () => {
    onApply({ ...draft });
    onClose();
  };

  const handleCancel = () => {
    setDraft({ ...filters });
    onClose();
  };

  return (
    <Drawer
      className="rounded-l-lg"
      direction="right"
      onClose={onClose}
      open={open}
      size={420}
    >
      <div className="flex h-screen flex-col">
        <div className="flex items-center justify-between border-b border-alpha-40 px-6 py-4">
          <p className="text-xl font-bold text-content-1000">Filters</p>
          <IconButton
            HugeIcon={Cancel01Icon}
            variant={IconButtonVariant.BASE}
            size={IconButtonSize.LG}
            borderLess
            className="!justify-end stroke-content-1000 !p-0"
            onClick={onClose}
          />
        </div>
        <div className="mx-6 border-b border-alpha-40 py-4">
          <Button variant={ButtonVariant.TEXT} onClick={handleClearAll}>
            Clear all
          </Button>
        </div>
        <div className="flex flex-grow flex-col gap-4 overflow-y-auto px-6 py-4">
          <PropertyMultiSelectDropdown
            label="Property"
            selectedOptions={draft.selectedProperties}
            onSelectionChange={(selected) =>
              setDraft((prev) => ({ ...prev, selectedProperties: selected }))
            }
          />
          {uniqueStates.length > 0 && (
            <MultiSelectDropdown
              label="State"
              options={uniqueStates}
              selectedOptions={draft.selectedStates}
              onSelectionChange={(selected) =>
                setDraft((prev) => ({ ...prev, selectedStates: selected }))
              }
            />
          )}
          <DateRange
            label="Last Activity"
            fromValue={draft.lastActivityFrom}
            toValue={draft.lastActivityTo}
            onFromChange={(value) =>
              setDraft((prev) => ({ ...prev, lastActivityFrom: value }))
            }
            onToChange={(value) =>
              setDraft((prev) => ({ ...prev, lastActivityTo: value }))
            }
          />
        </div>
        <div className="border-t border-alpha-40 px-6 py-4 flex gap-3">
          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            onClick={handleCancel}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.SM}
            onClick={handleApply}
            className="flex-1"
          >
            Apply
          </Button>
        </div>
      </div>
    </Drawer>
  );
};
